import { useCallback, useMemo } from 'react';

import usePolicies from '../../../shared/policies/hooks/usePolicies';
import { useProjectSettingsContext } from '../context';
import { useCanUserSetSBOMPolicy } from '../permissions';
import type { SelectOption } from '../../../../types';

import { UNSET_POLICY_OPTION } from './constants';
import type { ProjectIssueSettings } from './types';

export type SBOMProjectSettings = {
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onUpdate: (data: Partial<ProjectIssueSettings>) => void;
  isLoading: boolean;
  projectSettingsBaseURL: string;
  policyOptions: Array<SelectOption<number | null>>;
  selectedPolicyOption: SelectOption<number | null>;
  onSelectedPolicyChange: (policyId: number | null) => void;
  canEditPolicy: boolean;
  sbomAnalysisEnabled: boolean;
  setSBOMAnalysisEnabled: (sbomAnalysisEnabled: boolean) => void;
};

const useSBOMProjectSettings = () => {
  const {
    issues: {
      locator,
      settings,
      isDirty,
      isSaving,
      onSave,
      onUpdate,
      projectSettingsBaseURL,
    },
  } = useProjectSettingsContext();
  const { policies, isLoading } = usePolicies();
  return {
    isDirty,
    isSaving,
    onSave,
    onUpdate,
    isLoading,
    projectSettingsBaseURL,
    isSaveButtonDisabled: !isDirty || isLoading || isSaving,
    policyOptions: policies.SBOM,
    selectedPolicyOption: useMemo(() => policies.SBOM.find((policy) =>
      policy.value === settings.sbomPolicyId) ?? UNSET_POLICY_OPTION, [policies, settings.sbomPolicyId]),
    onSelectedPolicyChange: useCallback((policyId: number | null) =>
      onUpdate({ sbomPolicyId: policyId }), [onUpdate]),
    canEditPolicy: useCanUserSetSBOMPolicy(locator),
    sbomAnalysisEnabled: settings.sbomAnalysisEnabled,
    setSBOMAnalysisEnabled: useCallback((sbomAnalysisEnabled: boolean) =>
      onUpdate({ sbomAnalysisEnabled }), [onUpdate]),
  };
};

export default useSBOMProjectSettings;
